import React, { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { BankAccountsStatus, Invoice, TransactionSuggestion } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { formatCurrency } from "@/utils/paymentUtils";
import { settleSuggestionFetch } from "@/utils/matchSuggestionFallback";
import { useZeroMatchAcknowledgment } from "@/hooks/useZeroMatchAcknowledgment";
import { useMatchModalViewState, useMatchModalTitle } from "@/hooks/useMatchModalViewState";
import ExternalSettlementFields from "@/components/shared/ExternalSettlementFields";
import { parseActualSekAmount } from "@/components/shared/actualSekAmount";
import MatchModalBody from "@/components/shared/MatchModalBody";

interface PayableMatchSuggestionsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  payable: Invoice | null;
  /**
   * Whether the company has any bank accounts connected. Gates the entire flow (issue #498):
   * `false` skips the transaction search entirely and goes straight to the trusted date-picker
   * (case a, unchanged); `true` always attempts a match first (cases b/c).
   *
   * `'loading'` / `'error'` (#498 whole-branch review finding I2) — `useBankAccounts()` reports
   * these while it hasn't resolved yet, or when the fetch failed / a bank consent expired
   * (`CONSENT_REQUIRED`). Both cases mean the SAME thing here: the system does not yet know
   * whether accounts are connected. Must NEVER be treated as `false` — that would silently route
   * a payable whose accounts are genuinely still connected (or were, until the consent expired)
   * through the trusted, unaudited case-a write. See {@link BankAccountsStatus}'s docstring for
   * the full invariant.
   */
  hasBankAccounts: BankAccountsStatus;
  /**
   * Same signature as InvoiceActionMenu's existing onPay, widened with an optional 4th
   * `isAcknowledgment` flag (issue #498). Case a (no bank accounts) and case b (real match, via
   * onConfirmMatch below) call sites are unchanged. Case c — the zero-match/"None of these
   * match" acknowledgment path — passes `isAcknowledgment=true` so
   * useInvoiceActions.handleSinglePay routes to paymentService.acknowledgeExternalPayment
   * instead of the plain updatePayableStatus write.
   */
  onPay?: (payableId: string, paymentDate: Date, actualSekAmount?: number, isAcknowledgment?: boolean) => void;
  /** Resolves to true on success, false on failure (already reported by the caller's own mechanism). */
  onConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
}

/**
 * Issue #462 — payable → transaction match suggestions. Issue #498 merged the former separate
 * "Paid externally" (plain date-picker) and "Match to transaction" entry points into this single
 * modal, gated by the new `hasBankAccounts` prop, with a fourth branch (the zero-match
 * acknowledgment path) added.
 *
 * Unlike the receivable/receipt/sales-report equivalents (see
 * src/utils/matchSuggestionFallback.ts), payables have no JIT scoring path at all — this modal
 * always reads the async match_jobs pipeline's precomputed suggestion directly via
 * PaymentService.getPrecomputedTransactionSuggestionsForPayable, both on open and on "Search
 * again" (a plain re-fetch, not a JIT trigger — useful since the match-job cron may have scored
 * the payable since the modal opened). This fetch only ever runs when `hasBankAccounts` is true
 * — when false, the modal skips it entirely and renders the trusted date-picker immediately
 * (case a), identical to today's no-accounts-connected experience.
 *
 * Four states when `hasBankAccounts` is true (design spec's Flows 2/3/4/5/6):
 *  - still checking (fetch in flight) — a distinct loading state; the acknowledgment checkbox
 *    must never render before the fetch resolves (AC7).
 *  - loadFailed — a distinct "couldn't check for matches, try again" retry state; never
 *    conflated with a genuinely empty result (AC8, and the pre-existing #462 finding-3 note
 *    below).
 *  - candidates found — the existing suggestion table (case b, unchanged), plus a "None of
 *    these match" escape hatch (AC6) that transitions into the same acknowledgment flow used
 *    for zero candidates.
 *  - zero candidates (or "None of these match" was clicked) — plain-language text plus a single
 *    fixed-copy checkbox (ACKNOWLEDGMENT_COPY above) that must be ticked before the date-picker
 *    Confirm control is enabled (AC4/AC5). The foreign-currency settled-amount field is shared
 *    with case a via `renderPaymentDetailsFields` — reused, not duplicated (AC9).
 *
 * Final #462 whole-branch review, finding 3 (still applies under #498): a FAILED fetch must
 * never be conflated with a genuinely empty result — both the date-picker fallback (case a-style
 * write) and the acknowledgment checkbox (case c write) are real financial-state actions, so
 * silently reaching either on a fetch error (backend 500, expired session, network blip) would
 * tell the consultant "no candidate exists" when the truth is "we don't know". `loadFailed` is
 * tracked separately from `suggestions` so the two cases render distinct UI (see
 * `.claude/rules/client-error-handling.md`'s Tier 2 pattern, applied here even though this
 * component lives in `src/`, not `apps/client/`).
 */
function fetchSuggestions(payableId: string): Promise<TransactionSuggestion[]> {
  return ServiceRegistry.getPaymentService().getPrecomputedTransactionSuggestionsForPayable(payableId);
}

const PayableMatchSuggestionsModal: React.FC<PayableMatchSuggestionsModalProps> = ({
  open,
  setOpen,
  payable,
  hasBankAccounts,
  onPay,
  onConfirmMatch,
}) => {
  const t = useTranslations("consultantMatching.payable");
  const getMatchModalTitle = useMatchModalTitle();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [actualSekAmountInput, setActualSekAmountInput] = useState("");
  const [suggestions, setSuggestions] = useState<TransactionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  // Shared with receivables' MarkAsReceivedModal (issue #498) — see the hook's own docstring.
  /* jscpd:ignore-start — identical hook-call destructure is the point: both callers share the
   * exact same useZeroMatchAcknowledgment contract by design (issue #498's "Consistency across
   * document types" requirement). Not further extractable without merging the two components. */
  const {
    loadFailed, setLoadFailed, noneOfTheseMatch, acknowledged, setAcknowledged,
    reset: resetAcknowledgment, onNoneOfTheseMatch: handleNoneOfTheseMatch, onBackToSuggestions: handleBackToSuggestions,
  } = useZeroMatchAcknowledgment(() => setSelectedTransactionId(null));
  /* jscpd:ignore-end */

  // Pure async fetch — no synchronous setState in its own body, only inside
  // settleSuggestionFetch's .then/.catch/.finally callbacks, so calling it from a useEffect
  // never trips react-hooks/set-state-in-effect. The synchronous "reset before fetching"
  // setState calls live in the "adjust state during render" block below instead.
  const startSuggestionFetch = (payableId: string) => {
    settleSuggestionFetch(
      fetchSuggestions(payableId),
      { setSuggestions, setLoadFailed, setLoading },
      "Failed to fetch payable transaction suggestions:",
    );
  };

  const loadSuggestions = (payableId: string) => {
    setLoading(true);
    setSelectedTransactionId(null);
    resetAcknowledgment();
    startSuggestionFetch(payableId);
  };

  // "Adjust state during render" (React's own pattern, not a useEffect — avoids
  // react-hooks/set-state-in-effect; see FeedbackPanel.tsx for the established precedent in this
  // repo) resets the form each time the modal opens for a (possibly different) payable, closes,
  // or `hasBankAccounts` resolves while the modal stays open for the same payable (#498 finding
  // I2 — without tracking this third key too, a status transition from 'loading'/'error' to
  // `true` would skip resetting `loading`/`selectedTransactionId`, since only the fetch-trigger
  // effect below re-runs on that transition — the stale `loading=false` would then show "No
  // Matching Transaction Found" for an instant before the fetch's own setLoading(true) arrives).
  // prevOpen starts false so a modal that mounts already open still runs the reset.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevPayableId, setPrevPayableId] = useState<string | null>(null);
  const [prevHasBankAccounts, setPrevHasBankAccounts] = useState<BankAccountsStatus>(hasBankAccounts);
  if (open !== prevOpen || (payable?.id ?? null) !== prevPayableId || hasBankAccounts !== prevHasBankAccounts) {
    setPrevOpen(open);
    setPrevPayableId(payable?.id ?? null);
    setPrevHasBankAccounts(hasBankAccounts);
    if (open && payable) {
      // Prefill with the payable's current SEK estimate — confirming the default is a single
      // click; the consultant only edits it when the real settled amount differed.
      setActualSekAmountInput(payable.sekAmount != null ? String(payable.sekAmount) : "");
      // #498 Task 8 — reopening an already-resolved payable shows the PREVIOUSLY entered payment
      // date instead of defaulting to today (design spec Flow 7 step 2: "shows the previously
      // entered payment details"). A first-time mark-as-paid has no paymentDate yet, so this is a
      // no-op for that case (falls through to `new Date()`, unchanged from before this task).
      setSelectedDate(payable.paymentDate ? new Date(payable.paymentDate) : new Date());
      if (hasBankAccounts === true) {
        setLoading(true);
        setSelectedTransactionId(null);
        resetAcknowledgment();
      } else {
        // Case a (hasBankAccounts === false) or bank-status-unknown (#498 finding I2 —
        // hasBankAccounts is 'loading'/'error'): skip the fetch entirely, no matching language.
        // There is nothing to search yet while the system doesn't know if accounts are connected.
        setSuggestions([]);
        setLoadFailed(false);
        setLoading(false);
      }
    }
    /* jscpd:ignore-start — the close-reset block and the fetch-trigger effect below are
     * identical in shape to receivables' MarkAsReceivedModal by design (same five-state
     * contract, issue #498); only the per-field state setters differ in name, not structure.
     * Not further extractable without merging the two components. */
    if (!open) {
      setSuggestions([]);
      setSelectedTransactionId(null);
      setSelectedDate(new Date());
      resetAcknowledgment();
      setActualSekAmountInput("");
    }
  }

  // The actual async side effect (fetching suggestions) stays a real useEffect — its body
  // calls only the pure-async startSuggestionFetch, never the reset-plus-fetch loadSuggestions.
  useEffect(() => {
    if (open && payable && hasBankAccounts === true) {
      startSuggestionFetch(payable.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startSuggestionFetch is recreated every render, not a dependency
  }, [open, payable, hasBankAccounts]);
  /* jscpd:ignore-end */

  const handleSearchAgain = () => {
    if (!payable) return;
    loadSuggestions(payable.id);
  };

  const hasSuggestions = suggestions.length > 0;
  // Shared with receivables' MarkAsReceivedModal and receipts' MarkAsMatchedModal (issue #498)
  // — see the hook's own docstring for what each of the five states means. Called before the
  // `!payable` early return below — React Hooks must run unconditionally on every render, and
  // `payable` can transition from non-null to null while this modal stays open.
  const { showCandidateTable, showStillChecking, showLoadFailed, showBankStatusUnknown } =
    useMatchModalViewState({ hasBankAccounts, loading, loadFailed, hasSuggestions, noneOfTheseMatch });

  if (!payable) return null;

  const isForeignCurrency = payable.currency !== "SEK";
  const { amount: parsedActualSekAmount } = parseActualSekAmount(actualSekAmountInput);
  const isActualSekAmountValid = parsedActualSekAmount !== null;

  const handleConfirmClick = async () => {
    if (!selectedTransactionId || !onConfirmMatch) return;
    setConfirming(true);
    try {
      const success = await onConfirmMatch(payable.id, selectedTransactionId);
      if (success) {
        setOpen(false);
      }
    } finally {
      setConfirming(false);
    }
  };

  // Case a: no bank accounts connected — plain "paid externally" write, unchanged path. `onPay`
  // is fire-and-forget (see the case c comment on handleConfirmAcknowledgment below for why) —
  // this handler correctly reflects that: no loading state is faked around it, it just fires the
  // write and closes.
  const handleConfirmExternalPayment = () => {
    if (onPay) {
      onPay(payable.id, selectedDate, isForeignCurrency ? parsedActualSekAmount : undefined);
    }
    setOpen(false);
  };

  // Case c: zero-match / "None of these match" acknowledgment write (issue #498). `onPay` (→
  // useInvoiceActions.handleSinglePay) is fire-and-forget by design — it owns its own try/catch,
  // its own success/failure toast, and its own refreshData() call, and never rethrows to the
  // caller. There is nothing for this handler to await, so it mirrors handleConfirmExternalPayment
  // (case a) exactly: fire the write and close immediately. A previous version of this handler
  // toggled a local `acknowledging` state around the synchronous `onPay` call to drive a
  // "Confirming..." button label — since there was no await, both `setAcknowledging` calls landed
  // in the same React batch and the loading state could never actually render. Removed rather than
  // faked; see the case a comment for the shared rationale.
  const handleConfirmAcknowledgment = () => {
    if (!acknowledged) return;
    if (onPay) {
      onPay(payable.id, selectedDate, isForeignCurrency ? parsedActualSekAmount : undefined, true);
    }
    setOpen(false);
  };

  // AC9 — the foreign-currency settled-amount field is shared verbatim between case a (no
  // accounts connected) and case c (zero-match acknowledgment), not duplicated — and with
  // receivables' MarkAsReceivedModal via ExternalSettlementFields (issue #498 / FIN-1023).
  const renderPaymentDetailsFields = (dateLabel?: string) => (
    <ExternalSettlementFields
      dateLabel={dateLabel}
      selectedDate={selectedDate}
      onSelectDate={setSelectedDate}
      showSekAmountField={isForeignCurrency}
      actualSekAmountInput={actualSekAmountInput}
      onActualSekAmountInputChange={setActualSekAmountInput}
      sekAmountFieldId="payable-actual-sek-amount"
    />
  );

  const title = getMatchModalTitle(
    { showBankStatusUnknown, showLoadFailed, showCandidateTable, showStillChecking },
    hasBankAccounts,
    t("caseA"),
  );

  /* jscpd:ignore-start — the MatchModalBody call site (shell props, forwarded local state, and
   * the component's own closing boilerplate) is identical in shape to receivables' and
   * receipts' call sites below the same props. Not further extractable without merging
   * payables/receivables/receipts into one generic component, which is a separate, larger,
   * higher-risk change (touches each page's own onPay/onMatchTransaction/
   * onMarkMatchedExternally call signatures) deliberately kept out of this PR's scope. */
  return (
    <MatchModalBody
      open={open}
      setOpen={setOpen}
      title={title}
      subtitle={<>{payable.vendor} — {formatCurrency(payable.amount, payable.currency)}</>}
      hasBankAccounts={hasBankAccounts}
      showBankStatusUnknown={showBankStatusUnknown}
      showStillChecking={showStillChecking}
      showLoadFailed={showLoadFailed}
      showCandidateTable={showCandidateTable}
      onRetry={handleSearchAgain}
      caseAContent={renderPaymentDetailsFields(t("dateLabel"))}
      onConfirmCaseA={handleConfirmExternalPayment}
      caseAConfirmLabel={t("confirm")}
      caseAConfirmDisabled={isForeignCurrency && !isActualSekAmountValid}
      suggestions={suggestions}
      selectedSuggestionId={selectedTransactionId}
      onSelectSuggestion={(suggestion) => setSelectedTransactionId(suggestion.transactionId)}
      formatCurrency={formatCurrency}
      onSearchAgain={handleSearchAgain}
      onNoneOfTheseMatch={handleNoneOfTheseMatch}
      onConfirmMatch={handleConfirmClick}
      confirmMatchDisabled={!selectedTransactionId || confirming}
      confirmMatchBusy={confirming}
      acknowledgeCheckboxId="acknowledge-unmatched-payment"
      acknowledged={acknowledged}
      onAcknowledgedChange={setAcknowledged}
      caseCContent={renderPaymentDetailsFields()}
      noneOfTheseMatch={noneOfTheseMatch}
      onBackToSuggestions={handleBackToSuggestions}
      onConfirmAcknowledgment={handleConfirmAcknowledgment}
      confirmAcknowledgmentDisabled={!acknowledged || (isForeignCurrency && !isActualSekAmountValid)}
    />
  );
};
/* jscpd:ignore-end */

export default PayableMatchSuggestionsModal;
